import { AfterContentChecked, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, switchMap, tap } from 'rxjs/operators';
import { DialogService } from '../../dialog/dialog.service';
import { dateAsString, dayAsString } from '../../shared/date.helper';
import { Inspection, UnderwritingStatus } from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { UserService } from '../../user/user.service';
import { InspectionService } from '../inspection.service';
import { Filter, FilterType, SearchFilterComponent } from '../../search.filter/search-filter.component';
import { VersionService } from '../../version/version.service';
import { get } from 'lodash';
import { InspectionStatus, statusAsText, underwritingStatusAsText } from '../inspection.status';
import { configuredFilters } from './filter-configurations';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { ProductTypeService } from '../../product.type/product-type.service';
import { getOrderIconClasses } from '../../shared/sort-order-icon.helper';
const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';

@Component({
  selector: 'app-inspection-search-results',
  templateUrl: './inspection-search-results.component.html',
  styleUrls: ['./inspection-search-results.component.scss'],
})
export class InspectionSearchResultsComponent implements OnInit, AfterContentChecked {
  inspections: Inspection[] = [];
  page = 1;
  pageSize = 10;
  count = 0;
  currentFilters = {};
  loading = false;
  definedFilters: Filter[] = configuredFilters;
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;
  private subscriptions: Subscription = new Subscription();
  fieldMapper = {
    customerName: {
      path: 'customer.name',
      sort: 'customer.name',
      label: 'Customer Name',
      name: 'customerName',
    },
    address: {
      path: 'inspection.address',
      sort: 'location.address',
      label: 'Address',
      name: 'address',
    },
    city: {
      path: 'inspection.city',
      sort: 'location.city',
      label: 'City',
      name: 'city',
    },
    state: {
      path: 'inspection.state',
      sort: 'location.state',
      label: 'State',
      name: 'state',
    },
    zip: {
      path: 'zip',
      sort: 'location.zip',
      label: 'Zip',
      name: 'zip',
    },
    archivedStatus: {
      path: 'inspection.archived',
      sort: 'inspection.archived',
      label: 'Archived',
      name: 'archivedStatus',
    },
    policyNumber: {
      path: 'policy.number',
      sort: 'policy.number',
      label: 'Policy Number',
      name: 'policyNumber',
    },
    productType: {
      path: 'location.policy.productType.name',
      label: 'Product Type',
      name: 'productType',
    },
    brokerName: {
      path: 'location.policy.broker.name',
      sort: 'broker.name',
      label: 'Broker Name',
      name: 'brokerName',
    },
    policyEffectiveDate: {
      path: 'policyEffectiveDate',
      sort: 'policy.effectiveDate',
      label: 'Policy Effective Date',
      name: 'policyEffectiveDate',
    },
    contactName: {
      path: 'contactName',
      sort: 'location.contactName',
      label: 'Contact Name',
      name: 'contactName',
    },
    contactEmail: {
      path: 'contactEmail',
      sort: 'location.contactEmail',
      label: 'Contact Email',
      name: 'contactEmail',
    },
    contactPhone: {
      path: 'contactPhone',
      sort: 'location.contactPhone',
      label: 'Contact Phone',
      name: 'contactPhone',
    },
    locationId: {
      path: 'locationId',
      sort: 'locationId',
      label: 'Location ID',
      name: 'locationId',
    },
    inspectorName: {
      path: 'inspector.name',
      sort: 'inspector.firstName',
      label: 'Inspector Name',
      name: 'inspectorName',
    },
    dueDate: {
      path: 'dueDate',
      sort: 'dueDate',
      label: 'Due Date',
      name: 'dueDate',
    },
    lastSynced: {
      path: 'lastSynced',
      sort: 'lastSynced',
      label: 'Sync Date',
      name: 'lastSynced',
    },
    status: {
      path: 'status',
      sort: 'status',
      label: 'Status',
      name: 'status',
    },
    underwritingStatus: {
      path: 'underwritingStatus',
      label: 'Underwriting Status',
      name: 'underwritingStatus',
    },
    purpose: {
      path: 'purpose',
      sort: 'purpose',
      label: 'Purpose',
      name: 'purpose',
    },
    version: {
      path: 'versionId',
      label: 'Version',
      name: 'version',
    },
    itemType: {
      path: 'items',
      sort: 'item.name',
      label: 'Item Type',
      name: 'itemType',
    },
    customItemName: {
      path: 'items',
      sort: 'items.names',
      label: 'Custom Item Name',
      name: 'customItemName',
    },
    location: {
      path: 'coordinates',
      sort: 'location.coordinates',
      label: 'Coordinates (lat, long)',
      name: 'location',
    },
    recommendationDueDate: {
      path: 'violationActionRequiredDeadline',
      sort: 'violationActionRequiredDeadline',
      label: 'Recommendation Due Date',
      name: 'recommendationDueDate',
    },
    vendorName: {
      path: 'vendor.name',
      sort: 'vendor.firstName',
      label: 'Vendor',
      name: 'vendorName',
    },
    assigneeName: {
      path: 'assignee.name',
      sort: 'assignee.firstName',
      label: 'Assignee',
      name: 'assigneeName',
    },
  };
  defaultFields = [
    this.fieldMapper['customerName'],
    this.fieldMapper['address'],
    this.fieldMapper['city'],
    this.fieldMapper['state'],
    this.fieldMapper['policyNumber'],
    this.fieldMapper['archivedStatus'],
    this.fieldMapper['status'],
  ];
  otherFields = [];

  @ViewChild(SearchFilterComponent, { static: true }) searchFilterComponent: SearchFilterComponent;

  get orderBy() {
    return localStorage.getItem('inspection-accepted-order') || 'inspection.completed';
  }

  set orderBy(value: string) {
    localStorage.setItem('inspection-accepted-order', value);
  }

  get asc() {
    return localStorage.getItem('inspection-accepted-asc')
      ? localStorage.getItem('inspection-accepted-asc') === 'true'
      : true;
  }

  set asc(value: boolean) {
    localStorage.setItem('inspection-accepted-asc', value + '');
  }
  productTypes: any[] = [];
  canAdd = false;
  canReview = false;
  params;
  loadDebounced = new Subject();
  dateAsString = dateAsString;

  constructor(
    public inspectionService: InspectionService,
    public userService: UserService,
    private toastr: ToastrService,
    private router: Router,
    private dialog: DialogService,
    public versionService: VersionService,
    private changeDetector: ChangeDetectorRef,
    private route: ActivatedRoute,
    public productTypeService: ProductTypeService,
  ) {}

  getEnumToIdLabel(enumType) {
    const enumValues = Object.values(enumType);
    const statusArray = [];
    enumValues.forEach((value, index) => {
      if (typeof value === 'number') {
        if (enumType === InspectionStatus) {
          // Skip DEPRECATED_COMPLETED status as it's no longer used
          if (value === InspectionStatus.DEPRECATED_COMPLETED) return;
          statusArray.push({ id: value, label: statusAsText(value) });
        } else if (enumType === UnderwritingStatus) {
          statusArray.push({ id: value, label: underwritingStatusAsText(value) });
        }
      }
    });
    return statusArray;
  }

  async ngOnInit() {
    const statusArray = this.getEnumToIdLabel(InspectionStatus);
    this.definedFilters.find(filter => filter.name === 'status').options = statusArray as any;

    const underwritingStatusArray = this.getEnumToIdLabel(UnderwritingStatus);
    this.definedFilters.find(filter => filter.name === 'underwritingStatus').options = underwritingStatusArray as any;
    [this.productTypes] = await this.productTypeService.list();
    this.productTypes = this.productTypes.map((productType: any) => ({
      label: productType.name,
      id: productType.name.toLowerCase(),
    }));
    this.definedFilters.find(filter => filter.name === 'productType').options = this.productTypes as any;

    const [activeVersions, archivedVersions] = await Promise.all([
      this.versionService.list(),
      this.versionService.archived(),
    ]);

    const allVersions = [...activeVersions, ...archivedVersions];
    this.definedFilters.find(filter => filter.name === 'version').options = allVersions.map(
      ({ id, name, isValid }) => ({
        id: name,
        label: isValid === false ? `${name} (invalid)` : name,
        disabled: isValid === false,
      }),
    );

    const allItemNames = await this.versionService.allItemNames();

    const itemTypes = allItemNames.map(name => ({ id: name, label: name }));
    this.definedFilters.find(filter => filter.name === 'itemType').options = itemTypes;

    this.canReview = await this.userService.hasPermissions([permissions.INSPECTION.REVIEW]);

    this.subscriptions.add(
      this.route.queryParams.subscribe(async params => {
        this.loading = true;
        this.params = null;
        this.changeDetector.markForCheck();
        await this.updateParams(params);
      }),
    );
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      this.loadDebounced
        .pipe(
          tap(),
          debounceTime(1000),
          tap(),
          switchMap(async () => {
            this.loadDataLive();
          }),
          catchError(error => {
            console.error('Error in stream:', error);
            return EMPTY;
          }),
        )
        .subscribe(),
    );
  }

  ngAfterContentChecked() {
    this.changeDetector.detectChanges();
  }

  loadData() {
    this.loadDebounced.next();
  }

  async updateParams(params: any) {
    this.params = params;
    this.page = Number(params['page']) || 1;
    this.pageSize = Number(params['pageSize']) || 10;
    this.asc = params['asc'] ? params['asc'] === 'true' : true;
    this.orderBy = params['orderBy'] || 'inspection.completed';

    const { asc, orderBy, page, pageSize, ...filterParams } = params;
    this.currentFilters = { ...filterParams };

    this.changeDetector.detectChanges();
    this.loadData();
  }

  async loadDataLive() {
    const params = {
      ...this.getParams(),
      ...{ archivedStatus: 'both' },
      ...this.params,
    };
    let rows: any[];
    [rows, this.count] = await this.inspectionService.list(params);
    this.inspections = rows.map(i => ({
      ...i,
      day: i.dueDate ? dayAsString(new Date(i.dueDate)) : '',
    }));
    this.loading = false;
    this.changeDetector.detectChanges();
  }

  getParams() {
    return {
      page: this.page,
      pageSize: this.pageSize,
      orderBy: this.orderBy,
      asc: this.asc,
      // archivedStatus: 'both',
    };
  }

  onPageChanged(page: number) {
    this.page = page;
    this.navigate();
  }

  setOrder(field: string) {
    if (field) {
      this.asc = this.orderBy === field ? !this.asc : true;
      this.orderBy = field;
      this.page = 1;
      this.navigate();
    }
  }

  getOrderIcon(field: string) {
    return getOrderIconClasses(field, this.orderBy, this.asc);
  }

  open(ev, id: number) {
    if (ev.target.tagName === 'TD') {
      this.router.navigateByUrl(`/inspection/form/${id}`);
    }
  }

  async filtersUpdated(filters: any) {
    this.currentFilters = {};
    this.otherFields = [];

    for (const filter of filters.filter(f => f.key !== null)) {
      this.currentFilters[filter.key] = filter.value;
      if (filter.operator) {
        this.currentFilters[filter.key + 'Operator'] = filter.operator;
      }
      if (!this.defaultFields.find(f => f.name === filter.key) && !this.otherFields.find(f => f.name === filter.key)) {
        if (this.otherFields.length < 3) {
          this.otherFields.push(this.fieldMapper[filter.key]);
        }
      }
    }
    this.changeDetector.markForCheck();

    this.loading = true;
    this.navigate();

    setTimeout(() => (this.loading = false), 400);
  }

  resetAll() {
    this.router.navigate([], {
      queryParams: {
        ...this.getParams(),
      },
      relativeTo: this.route,
    });
  }

  hasFilters() {
    if (Object.keys(this.currentFilters).length > 0) {
      return true;
    }
  }

  getValueFromPath(item, name, path) {
    if (name === 'version') {
      // since api doesn't return the version name, in the table just display the selected version name
      return this.currentFilters['version'] || '';
    }

    if (
      name === 'dueDate' ||
      name === 'policyEffectiveDate' ||
      name === 'lastSynced' ||
      name === 'recommendationDueDate'
    ) {
      return dateAsString(get(item, path));
    }

    if (name === 'itemType') {
      const items = get(item, path);
      return items.map(i => i.item.name).join(', ');
    }

    if (name === 'customItemName') {
      const items = get(item, path);
      return items
        .flatMap(i => (Array.isArray(i.names) ? i.names : []))
        .filter(name => name && name.trim() !== '')
        .join(', ');
    }

    if (name === 'underwritingStatus') {
      return underwritingStatusAsText(get(item, path));
    }

    if (name === 'location') {
      const location = get(item, path);
      if (Array.isArray(location) && location.length === 2) {
        return `${location[1]}, ${location[0]}`;
      }
      return '';
    }

    return get(item, path);
  }

  getHighlightTerm(fieldName: string): string | null {
    const value = this.currentFilters[fieldName];
    if (value === undefined || value === null || value === '') {
      return null;
    }

    const filterConfig = this.definedFilters.find(f => f.name === fieldName);
    if (!filterConfig) {
      return null;
    }

    if (filterConfig.type !== FilterType.TEXTWITHOR && filterConfig.type !== FilterType.TEXT) {
      return null;
    }

    return String(value);
  }

  getDisplayValue(row: any, field: any): string {
    if (field.name === 'archivedStatus') {
      if (this.isArchived(row)) {
        return 'Yes';
      } else {
        return 'No';
      }
    }

    if (field.name === 'status') {
      return statusAsText(get(row, field.path));
    }

    return row[field.name];
  }

  isArchived(row: any): boolean {
    return row.archived;
  }

  downloadCsv() {
    this.inspectionService.exportCsv(this.currentFilters);
  }

  navigate() {
    if (Object.keys(this.currentFilters).length === 0) {
      this.router.navigate([], {
        queryParams: {
          ...this.getParams(),
        },
        relativeTo: this.route,
      });
    } else {
      this.router.navigate([], {
        queryParams: {
          ...this.getParams(),
          ...this.currentFilters,
        },
        relativeTo: this.route,
      });
    }
  }

  redirectReview(inspection) {
    if (inspection) {
      // Validations problems must be fixed before entering this screen
      if (inspection.status === InspectionStatus.INVALID_SYNC) {
        this.router.navigateByUrl(`/inspection/review-invalid/${inspection.id}`);
        return;
      }

      // Inspection must be completed before entering this screen
      if (!inspection.completed) {
        this.router.navigateByUrl(`/inspection/form/${inspection.id}`);
        return;
      } else {
        this.router.navigate([`/inspection/review/main/${inspection.id}`], {
          replaceUrl: true,
          relativeTo: this.route,
        });
      }
    }
  }
}
